import {Modal} from '@/components/atoms'

/**
 * ConfirmationModal component renders a reusable modal for confirmation actions.
 * This component consolidates common modal patterns like cancel and save draft confirmations.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.showModal - Determines whether the modal is visible.
 * @param {function} props.setShowModal - Function to set the visibility of the modal.
 * @param {function} props.handleConfirm - Function to handle the confirmation action.
 * @param {string} props.title - The title text for the modal.
 * @param {string} props.text - The description text for the modal.
 * @param {string} [props.primaryButtonText] - Text for the primary button (default: 'Yes').
 * @param {string} [props.secondaryButtonText] - Text for the secondary button (default: 'No').
 * @param {string} [props.type] - Modal type (default: 'fluid').
 * @param {string} [props.className] - Additional CSS class name.
 * @param {boolean} [props.isDark] - Whether to use dark theme (default: true).
 * @param {Object} [props.primaryButtonProps] - Additional props for primary button.
 * @param {Object} [props.secondaryButtonProps] - Additional props for secondary button.
 *
 * @returns {JSX.Element|null} The ConfirmationModal component or null if showModal is false.
 */
export const ConfirmationModal = ({
  showModal,
  setShowModal,
  handleConfirm,
  title,
  text,
  primaryButtonText = 'Yes',
  secondaryButtonText = 'No',
  type = 'fluid',
  className,
  isDark = true,
  primaryButtonProps,
  secondaryButtonProps,
}) => {
  if (!showModal) return null

  const handleClose = () => setShowModal(false)

  // Default button props
  const defaultPrimaryButtonProps = {
    onClick: handleConfirm,
    size: 'medium',
    text: primaryButtonText,
    variant: 'primary',
  }

  const defaultSecondaryButtonProps = {
    onClick: handleClose,
    size: 'medium',
    text: secondaryButtonText,
    variant: 'secondary',
  }

  return (
    <Modal
      showModal={showModal}
      isDark={isDark}
      onCloseModal={handleClose}
      primaryButtonProps={primaryButtonProps || defaultPrimaryButtonProps}
      primaryButtonText={primaryButtonProps ? undefined : primaryButtonText}
      onClickPrimaryButton={primaryButtonProps ? undefined : handleConfirm}
      showSecondaryButton={true}
      secondaryButtonProps={secondaryButtonProps || defaultSecondaryButtonProps}
      secondaryButtonText={secondaryButtonProps ? undefined : secondaryButtonText}
      onClickSecondaryButton={secondaryButtonProps ? undefined : handleClose}
      showLink={false}
      showSlot={false}
      showTertiaryButton={false}
      text={text}
      title={title}
      type={type}
      className={className}
    />
  )
}
